import { ImageInput } from '../types';

/**
 * Utility functions for handling image inputs in the frontend
 */

/**
 * Convert a File object to base64 string
 * @param file - The image file to convert
 * @returns Promise with base64 string (without data URL prefix)
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      } else {
        reject(new Error('Failed to read file as base64'));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Validate image file size (max 3.75 MB as per AWS Bedrock limits)
 * @param file - The image file to validate
 * @returns true if valid, false otherwise
 */
export const validateImageSize = (file: File): boolean => {
  const maxSizeInBytes = 3.75 * 1024 * 1024; // 3.75 MB
  return file.size <= maxSizeInBytes;
};

/**
 * Validate image file type
 * @param file - The image file to validate
 * @returns true if valid type, false otherwise
 */
export const validateImageType = (file: File): boolean => {
  const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  return validTypes.includes(file.type);
};

/**
 * Get media type from file
 * @param file - The image file
 * @returns Media type string
 */
export const getMediaType = (file: File): ImageInput['mediaType'] => {
  const type = file.type as ImageInput['mediaType'];
  const validTypes: ImageInput['mediaType'][] = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  
  if (validTypes.includes(type)) {
    return type;
  }
  
  // Default to jpeg if type is not recognized
  return 'image/jpeg';
};

/**
 * Convert multiple File objects to ImageInput array
 * @param files - Array of image files
 * @returns Promise with array of ImageInput objects
 */
export const filesToImageInputs = async (files: File[]): Promise<ImageInput[]> => {
  const imagePromises = files.map(async (file) => {
    // Validate file
    if (!validateImageType(file)) {
      throw new Error(`Invalid image type: ${file.type}. Must be JPEG, PNG, GIF, or WebP.`);
    }
    
    if (!validateImageSize(file)) {
      throw new Error(`Image ${file.name} is too large. Maximum size is 3.75 MB.`);
    }
    
    // Convert to base64
    const base64Data = await fileToBase64(file);
    const mediaType = getMediaType(file);
    
    return {
      base64Data,
      mediaType
    };
  });
  
  return Promise.all(imagePromises);
};

/**
 * Validate dimensions of an image (max 8000px x 8000px as per AWS Bedrock)
 * @param file - The image file to validate
 * @returns Promise with validation result
 */
export const validateImageDimensions = (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      const maxDimension = 8000;
      const isValid = img.width <= maxDimension && img.height <= maxDimension;
      resolve(isValid);
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(false);
    };
    
    img.src = url;
  });
};

/**
 * Comprehensive validation for image files
 * @param files - Array of files to validate
 * @returns Promise with validation result and error message if invalid
 */
export const validateImages = async (
  files: File[]
): Promise<{ isValid: boolean; error?: string }> => {
  // Check number of images (max 20 as per AWS Bedrock)
  if (files.length > 20) {
    return {
      isValid: false,
      error: 'Maximum 20 images allowed per request'
    };
  }
  
  // Validate each file
  for (const file of files) {
    // Check type
    if (!validateImageType(file)) {
      return {
        isValid: false,
        error: `Invalid image type: ${file.type}. Supported types: JPEG, PNG, GIF, WebP`
      };
    }
    
    // Check size
    if (!validateImageSize(file)) {
      return {
        isValid: false,
        error: `Image "${file.name}" exceeds maximum size of 3.75 MB`
      };
    }
    
    // Check dimensions
    const dimensionsValid = await validateImageDimensions(file);
    if (!dimensionsValid) {
      return {
        isValid: false,
        error: `Image "${file.name}" exceeds maximum dimensions of 8000px x 8000px`
      };
    }
  }
  
  return { isValid: true };
};
